import { defineStore } from 'pinia';
import { get, cloneDeep, findIndex, find, each, map, has, startsWith } from 'lodash';
import Vue from 'vue';
import uniqid from 'uniqid';
import axios from 'axios';

import { set, unset } from '@/helpers';

import { getUnixTime } from 'date-fns';

import TagApi from '@/store/api/cms/tag';
import FileApi from '@/store/api/cms/file';
import MenuApi from '@/store/api/cms/menu';
import PageApi from '@/store/api/cms/page';
import SectionApi from '@/store/api/cms/section';
import CategoryApi from '@/store/api/cms/category';
import CategoryFiltersAPI from '@/store/api/cms/categoryFilter';
import MemberTagApi from '@/extensions/restrictedArea/store/api/memberTag';

import EXT_EVNT_CategoryApi from '@/extensions/eventManager/store/api/category';
import EXT_SHOP_CategoryApi from '@/extensions/shop/store/api/category';
import EXT_SHOP_CustomerGroupApi from '@/extensions/shop/store/api/customerGroup';
import EXT_SHOP_PropertyApi from '@/extensions/shop/store/api/property';
import EXT_SHOP_TagApi from '@/extensions/shop/store/api/tag';
import EXT_MARKETING_CampaignApi from '@/extensions/marketing/store/api/campaign';
import EXT_NEWSLETTER_SubscriptionTopicApi from '@/extensions/newsletter/store/api/subscriptionTopic';

import pinia from '@/pinia';
import { useSetupStore } from '@/stores';

function restoreMissingMediaFileData(sentSection, receivedSection) {
    if (!receivedSection || !sentSection) return receivedSection;

    const sentContents = get(sentSection, 'contents') || [];
    const receivedContents = get(receivedSection, 'contents') || [];

    if (!receivedContents.length || !sentContents.length) return receivedSection;

    const keyMatchCount = {};

    receivedContents.forEach(receivedContent => {
        const receivedMedia = get(receivedContent, 'media') || [];
        if (!receivedMedia.length) return;

        const key = receivedContent.key;
        if (keyMatchCount[key] === undefined) keyMatchCount[key] = 0;

        const sentContentsWithKey = sentContents.filter(c => c.key === key);
        const sentContent = sentContentsWithKey[keyMatchCount[key]];
        keyMatchCount[key]++;

        if (!sentContent) return;
        const sentMedia = get(sentContent, 'media') || [];

        receivedMedia.forEach((media, mediaIndex) => {
            if (media.type !== 'file' || media.file) return;

            const match = sentMedia[mediaIndex];
            if (match && match.file) {
                media.file = cloneDeep(match.file);
            }
        });
    });

    return receivedSection;
}

const defaultForm = {
    title: '',
    key: '',
    view: '',
    restriction: '',
    category: null,
    sections: [],
    categoryFilters: [],
    tags: [],
    metas: [],
    created_at: null,
    publish_start: null,
    publish_end: null
};

const defaultRules = {
    title: 'required',
    key: 'required|key',
    view: 'required',
    category: 'required',
    created_at: 'required'
};

const defaultUi = {
    activeTranslation: undefined,
    form: {
        initialized: false,
        validateOnEnter: false,
        sections: []
    }
};

export const usePageStore = defineStore('page', {
    state: () => ({
        loading: true,
        categoryPagesPaginated: {
            variables: {
                link: '/posts/',
                order: 'created_at',
                direction: 'desc',
                paginationSizes: [25, 50, 100]
            },
            filters: {},
            pageInfo: {
                total: 0,
                perPage: 25,
                currentPage: 1,
                lastPage: 0
            },
            allColumns: [],
            activeColumns: [],
            showFilters: false,
            defaultSort: null,
            link: {},
            items: []
        },
        options: {
            section: {
                loading: false,
                items: []
            },
            category: {
                loading: false,
                items: []
            },
            view: {
                loading: false,
                items: []
            },
            menu: {
                loading: false,
                items: []
            },
            tag: {
                loading: false,
                items: []
            },
            fileTag: {
                loading: false,
                items: []
            },
            filter: {
                loading: false,
                items: []
            },
            extensions: {
                eventManager: {
                    category: {
                        loading: false,
                        items: []
                    }
                },
                shop: {
                    category: {
                        loading: false,
                        items: []
                    },
                    customerGroup: {
                        loading: false,
                        items: []
                    },
                    property: {
                        loading: false,
                        items: []
                    },
                    tag: {
                        loading: false,
                        items: []
                    }
                },
                marketing: {
                    campaign: {
                        loading: false,
                        items: []
                    }
                },
                newsletter: {
                    subscriptionTopic: {
                        loading: false,
                        items: []
                    }
                },
                restrictedArea: {
                    memberTag: {
                        loading: false,
                        items: []
                    },
                }
            }
        },
        form: cloneDeep(defaultForm),
        rules: cloneDeep(defaultRules),
        ui: cloneDeep(defaultUi),
        isGlobalContentsMode: false
    }),

    getters: {
        formInitialized: (state) => state.ui.form.initialized,
        formValidateOnEnter: (state) => state.ui.form.validateOnEnter
    },

    actions: {
        setState({ path, value }) {
            set(this, path, value);
        },

        unsetState(path) {
            unset(this, path);
        },

        // List mutations
        addPaginatedCategoryPagesToState(data) {
            this.categoryPagesPaginated.items = data.items;
            this.categoryPagesPaginated.pageInfo = cloneDeep(data.pageInfo);
            this.categoryPagesPaginated.allColumns = cloneDeep(data.config.allColumns);
            this.categoryPagesPaginated.link = cloneDeep(data.config.link);
            this.categoryPagesPaginated.showFilters = get(data.config, 'showFilters') || false;
            this.categoryPagesPaginated.defaultSort = get(data.config, 'defaultSort') || null;
        },

        defineActiveColumns(activeColumnKeys) {
            let activeColumns = [];
            each(this.categoryPagesPaginated.allColumns, column => {
                let allowed = find(activeColumnKeys, value => column.key === value);
                if (allowed) activeColumns.push(column);
            });

            if (activeColumns.length) {
                this.categoryPagesPaginated.activeColumns = activeColumns;
            }
        },

        defineItemsPerPage(perPage) {
            this.categoryPagesPaginated.pageInfo.perPage = perPage;
        },

        defineCurrentPage(currentPage) {
            this.categoryPagesPaginated.pageInfo.currentPage = currentPage;
        },

        defineFilterValue({ key, value }) {
            if (value.match === '' && value.like === '' && value.from === '' && value.to === '' && value.in.length === 0) {
                Vue.delete(this.categoryPagesPaginated.filters, key);
            } else {
                Vue.set(this.categoryPagesPaginated.filters, key, value);
            }
        },

        defineSorting({ key, value }) {
            Vue.set(this.categoryPagesPaginated.variables, 'order', key);
            Vue.set(this.categoryPagesPaginated.variables, 'direction', value);
        },

        defineLinkValue(value) {
            Vue.set(this.categoryPagesPaginated.variables, 'link', value);
        },

        defineFilterVisibility(value) {
            Vue.set(this.categoryPagesPaginated, 'showFilters', value);
        },

        updateLoading(value) {
            this.loading = value;
        },

        updateSectionLoading({ path, section, value }) {
            if (path === undefined) path = 'sections';
            const sections = get(this.form, path) || [];

            let s = sections.findIndex(el => el.id === section.id);

            if (s >= 0) {
                path = `${path}.${s}`;
            } else if (this.form.sections && this.form.sections.length) {
                const findPath = (sections, path) => {
                    if (!sections || !sections.length) return null;

                    const i = sections.findIndex(el => el.id === section.id);
                    if (i >= 0) {
                        return `${path}.${i}`;
                    } else {
                        let nestedPath;
                        sections.forEach((el, i) => {
                            if (nestedPath) return;
                            if (el.gridAreas && el.gridAreas.length) {
                                el.gridAreas.forEach((area, a) => {
                                    if (nestedPath) return;
                                    if (area.gridItems && area.gridItems.length) {
                                        nestedPath = findPath(area.gridItems, `${path}.${i}.gridAreas.${a}.gridItems`);
                                    }
                                });
                            }
                        });

                        if (nestedPath) {
                            return nestedPath;
                        }
                    }
                };

                path = findPath(this.form.sections, 'sections');
            }

            if (path) {
                if (value) set(this.form, `${path}.loading`, value);
                else unset(this.form, `${path}.loading`);
            }
        },

        updateRules(rules) {
            Vue.set(this, 'rules', rules);
        },

        resetRules() {
            let r = cloneDeep(defaultRules);

            const category = this.form.category;
            const page = this.form;

            if (category && category.id && page && page.id) {
                r.key = `required|key|unique_category_page_key:"category":${category.id},"page":${page.id}`;
            } else if (category && category.id) {
                r.key = `required|key|unique_category_page_key:"category":${category.id}`;
            }

            Vue.set(this, 'rules', r);
        },

        resetUI() {
            Vue.set(this, 'ui', cloneDeep(defaultUi));
        },

        updateSectionOptionsLoading(value) {
            Vue.set(this.options.section, 'loading', value);
        },

        insertSectionOptions(sections) {
            Vue.set(this.options.section, 'items', sections);
        },

        updateCategoryOptionsLoading(value) {
            Vue.set(this.options.category, 'loading', value);
        },

        insertCategoryOptions(categories) {
            Vue.set(this.options.category, 'items', categories);
        },

        updateViewOptionsLoading(value) {
            Vue.set(this.options.view, 'loading', value);
        },

        insertViewOptions(views) {
            Vue.set(this.options.view, 'items', views);
        },

        updateMenuOptionsLoading(value) {
            Vue.set(this.options.menu, 'loading', value);
        },

        insertMenuOptions(menus) {
            Vue.set(this.options.menu, 'items', menus);
        },

        updateTagOptionsLoading(value) {
            Vue.set(this.options.tag, 'loading', value);
        },

        insertTagOptions(tags) {
            Vue.set(this.options.tag, 'items', tags);
        },

        updateFileTagOptionsLoading(value) {
            Vue.set(this.options.fileTag, 'loading', value);
        },

        insertFileTagOptions(tags) {
            Vue.set(this.options.fileTag, 'items', tags);
        },

        updateFilterOptionsLoading(value) {
            Vue.set(this.options.filter, 'loading', value);
        },

        insertFilterOptions(filters) {
            Vue.set(this.options.filter, 'items', filters);
        },

        sortSections({ path, sections }) {
            if (path === undefined) path = 'sections';
            const currentSections = get(this.form, path) || [];

            sections.forEach((section, index) => {
                const s = findIndex(currentSections, { id: section.id });
                if (s >= 0) set(this.form, `${path}.${s}.position`, index + 1);
            });
        },

        // Form mutations
        setActiveTranslation(translation) {
            Vue.set(this.ui, 'activeTranslation', translation);
        },

        setFormInitialized(flag) {
            Vue.set(this.ui.form, 'initialized', flag);
        },

        setFormValidateOnEnter(flag) {
            Vue.set(this.ui.form, 'validateOnEnter', flag);
        },

        setFormTitle(value) {
            Vue.set(this.form, 'title', value);
        },

        setFormKey(value) {
            Vue.set(this.form, 'key', value);
        },

        setFormCategory(value) {
            Vue.set(this.form, 'category', value);
        },

        setFormRestriction(value) {
            Vue.set(this.form, 'restriction', value);
        },

        setFormView(value) {
            Vue.set(this.form, 'view', value);
        },

        setFormCreatedAt(value) {
            Vue.set(this.form, 'created_at', value);
        },

        setFormPublishStart(value) {
            Vue.set(this.form, 'publish_start', value);
        },

        setFormPublishEnd(value) {
            Vue.set(this.form, 'publish_end', value);
        },

        // Category filters
        setFormFilterPivot({ index, value }) {
            Vue.set(this.form.categoryFilters[index].pivot, 'value', value);
        },

        addNewFilterInput(filter) {
            if (this.form.categoryFilters.length) {
                const match = this.form.categoryFilters.find(oldFilter => oldFilter.pivot.value === filter.pivot.value);
                if (match) return;
            }
            let categoryFilters = cloneDeep(this.form.categoryFilters);
            Vue.set(this.form.categoryFilters, categoryFilters.length, filter);
        },

        removeFilterInput(index) {
            Vue.set(this.form.categoryFilters[index].pivot, 'deleted', true);
        },

        addContent({ s, config, content }) {
            const section = get(this.form, `sections.${s}`);

            if (section) {
                let value = {
                    id: get(content, 'id', uniqid('new:')),
                    key: get(content, 'key', config.key),
                    media: get(content, 'media', []),
                    files: get(content, 'files', []),
                    images: get(content, 'images', []),
                    categories: get(content, 'categories', []),
                    hyperlink: get(content, 'hyperlink', {}),
                    position: get(content, 'position', 0)
                };

                let fields = get(config, 'fields') || [];
                let key, settings, setting, options, option, hyperlink;
                fields.forEach(field => {
                    key = get(field, 'key');
                    switch (key) {
                        case 'hyperlink':
                            let type = 'internal';

                            settings = config.settings || [];
                            setting = settings.find(setting => setting.key === 'link_types');
                            options = get(setting, 'options') || [];
                            if (options.length) {
                                option = find(options, 'default');
                                if (option) type = option.key;
                            }

                            hyperlink = {
                                name: '',
                                href: '',
                                type,
                                target: '_self',
                                anchor: '',
                                download: false,
                                menuItem: {}
                            };
                            value.hyperlink = hyperlink;
                            break;

                        case 'color':
                            if (!field.value && field.options && field.options.length) {
                                option = field.options.find(el => ['true', '1', 'yes', true].includes(el.default));
                                if (option && option.value) value[key] = option.value;
                            }
                            break;

                        default:
                            if (key) value[key] = get(field, 'value', '');
                    }
                });

                const index = get(section, 'contents.length', 0);
                Vue.set(this.form.sections[s].contents, index, value);
            }
        },

        addSection({ s, path, section }) {
            const setupStore = useSetupStore(pinia);
            const cms = setupStore.activeCms;

            if (path === undefined) path = 'sections';
            const sections = get(this.form, path) || [];

            const sorted = cloneDeep(sections)
                .filter(el => !section.area || el.area === section.area)
                .sort((a, b) => get(a, 'position', 0) - get(b, 'position', 0));

            let position = s;
            const target = get(sorted, s);

            if (target) {
                sections.forEach((el, s) => {
                    if (el.position < target.position) return;
                    Vue.set(el, 'position', el.position + 1);
                });
            } else if (sorted.length) {
                position = sorted[sorted.length - 1].position;
            }

            position++;

            let type = 'content';
            let result = {};

            let contents = [];
            let categories = [];
            let eventCategories = [];
            let shopCategories = [];

            let columns, rows;
            let gridAreas = [];
            if (section.is === 'grid') {
                type = 'grid';

                const generateSettingsLikeStructure = el => {
                    let options = [];
                    if (el.value && _.isArray(el.value)) {
                        options = el.value.map(value => ({ key: value, value }));
                    } else if (el.value && _.isString(el.value)) {
                        const regex = new RegExp(/[^\s()]+(?:\([^\s()]+(?:\([^()]+\))?(?:, *[^\s()]+(?:\([^()]+\))?)*\))?/, 'g');
                        options = el.value.match(regex).map(value => ({ key: value, value }));
                    }

                    if (options && _.isArray(options)) {
                        if (!el.options) el.options = [];

                        options.forEach((option, i) => {
                            const matches = /^repeat\((?<count>\d+), (?<track>\w+)\) ?$/.exec(option.value);
                            if (matches) {
                                const { groups: { count, track } } = matches;
                                for (let i = 0; i < count; i++) el.options.push({ key: track, value: track });
                            } else {
                                el.options.push(option);
                            }
                        });

                        el.value = el.options.map(option => option.value).join(' ');
                    }
                };

                columns = _.cloneDeep(section.columns);
                if (columns && columns.length) {
                    columns.forEach(generateSettingsLikeStructure);
                }

                rows = _.cloneDeep(section.rows);
                if (rows && rows.length) {
                    rows.forEach(generateSettingsLikeStructure);
                }

                gridAreas = section.areas.map(area => {
                    return {
                        id: uniqid('new:'),
                        key: area.key,
                        name: area.name,
                        row: area.row,
                        column: area.column,
                        gridItems: [],
                    };
                });

                result = {
                    id: section.id || uniqid('new:'),
                    key: section.key,
                    area: section.area,
                    name: section.name,
                    type,
                    gridAreas,
                    rows,
                    columns,
                    position
                };
            } else {
                if (section.contents && section.contents.length) {
                    section.contents
                        .sort((a, b) => get(a, 'position', 0) - get(b, 'position', 0))
                        .forEach(el => {
                            if (el.initialize === false) return;

                            let content = {
                                id: uniqid('new:'),
                                key: el.key,
                                files: [],
                                images: [],
                                categories: [],
                                hyperlink: {}
                            };

                            get(el, 'fields', []).forEach(field => {
                                const key = get(field, 'key');
                                const settings = (get(el, 'settings') || []).filter(setting => setting.field === key);

                                switch (key) {
                                    case 'hyperlink':
                                        {
                                            let type = 'internal';

                                            const linkTypeSetting = settings.find(setting => setting.key === 'link_types');
                                            if (linkTypeSetting && linkTypeSetting.options && linkTypeSetting.options.length) {
                                                const defaultOption =
                                                    linkTypeSetting.options.find(type => type.default) || linkTypeSetting[0];
                                                type = defaultOption.key;
                                            }

                                            const hyperlink = {
                                                name: '',
                                                href: '',
                                                type,
                                                target: '_self',
                                                anchor: '',
                                                download: false,
                                                menuItem: {}
                                            };
                                            content.hyperlink = hyperlink;
                                        }
                                        break;
                                    case 'color':
                                        if (!field.value && field.options && field.options.length) {
                                            const option = field.options.find(el =>
                                                ['true', '1', 'yes', true].includes(el.default)
                                            );
                                            if (option && option.value) content[key] = option.value;
                                        }
                                        break;
                                    case 'images':
                                    case 'title':
                                    case 'sub_title':
                                    case 'text':
                                        break;
                                    default:
                                        if (key) content[key] = '';
                                }
                            });

                            contents.push(content);
                        });
                }

                categories = [];
                eventCategories = [];
                shopCategories = [];

                result = {
                    id: section.id || uniqid('new:'),
                    key: section.key,
                    area: section.area,
                    name: section.name,
                    type,
                    eventCategories,
                    shopCategories,
                    categories,
                    tags: [],
                    products: [],
                    articles: [],
                    pages: [],
                    contents,
                    position
                };
            }

            const destination = get(this.form, path);
            if (destination === undefined) set(this.form, path, []);
            set(this.form, `${path}.${sections.length}`, result);
        },

        replaceSection({ path, oldSection, newSection }) {
            if (path === undefined) path = 'sections';
            const sections = get(this.form, path) || [];

            let s = sections.findIndex(el => el.id === oldSection.id);
            if (s >= 0) set(this.form, `${path}.${s}`, cloneDeep(newSection));
        },

        deleteSection({ path, section }) {
            if (path === undefined) path = 'sections';
            const sections = get(this.form, path) || [];

            const s = findIndex(sections, el => el.id === section.id);
            if (s >= 0) unset(this.form, `${path}.${s}`);
        },

        addHyperlink() {
            const hyperlink = {
                name: '',
                href: '',
                type: 'internal',
                target: '_self',
                anchor: '',
                download: false,
                menuItem: {}
            };
            Vue.set(this.form, 'hyperlink', hyperlink);
        },

        addTag(name) {
            let tag;

            if (this.options.tag.items.length) {
                const match = this.options.tag.items.find(tag => tag.name === name);
                if (match) tag = match;
            }

            if (this.form.tags.length) {
                const match = this.form.tags.find(tag => tag.name === name);
                if (match) return;
            }

            if (name) {
                if (!tag) tag = { name };
                const i = this.form.tags.length;
                Vue.set(this.form.tags, i, tag);
            }
        },

        removeTag(tag) {
            const index = findIndex(this.form.tags, { name: tag.name });
            if (index >= 0) Vue.delete(this.form.tags, index);
        },

        setFormMeta(meta) {
            const i = this.form.metas.findIndex(el => el.key === meta.key);
            if (i >= 0) Vue.set(this.form.metas, i, meta);
            else Vue.set(this.form.metas, this.form.metas.length, meta);
        },

        setFormAddressFieldValue({ s, c, address }) {
            let a;
            const addresses = get(this.form, `sections[${s}].contents[${c}].addresses`);
            if (addresses === undefined) {
                Vue.set(this.form.sections[s].contents[c], 'addresses', []);
                a = 0;
            } else if (address.id) {
                a = findIndex(addresses, { id: address.id });
                if (a < 0) a = addresses.length;
            }

            if (a >= 0) {
                Vue.set(this.form.sections[s].contents[c].addresses, a, address);
            }
        },

        setFormRedirectField({ field, value }) {
            if (!get(this.form, `redirect`)) {
                const redirect = {
                    name: '',
                    href: '',
                    type: 'internal',
                    target: '_self',
                    anchor: '',
                    page: {},
                    download: false,
                    menuItem: {}
                };
                Vue.set(this.form, 'redirect', redirect);
            }

            Vue.set(this.form.redirect, field, value);
            Vue.set(this.form, 'reference', null);
        },

        setFormSettingsValue({ key, value }) {
            const settings = get(this.form, 'settings');

            if (!settings) {
                Vue.set(this.form, 'settings', []);
                Vue.set(this.form.settings, 0, { key, value });
            } else if (settings && settings.length === 0) {
                Vue.set(this.form.settings, 0, { key, value });
            } else {
                const index = findIndex(settings, { key });
                if (index >= 0) {
                    Vue.set(this.form.settings, index, { key, value });
                } else {
                    Vue.set(this.form.settings, settings.length, { key, value });
                }
            }
        },

        setFormSettingsOptions({ key, options }) {
            const settings = get(this.form, 'settings');
            if (!settings) {
                Vue.set(this.form, 'settings', []);
                Vue.set(this.form.settings, 0, { key, options });
            } else if (settings && settings.length === 0) {
                Vue.set(this.form.settings, 0, { key, options });
            } else {
                const index = findIndex(settings, { key });
                if (index >= 0) {
                    Vue.set(this.form.settings, index, { key, options });
                } else {
                    Vue.set(this.form.settings, settings.length, { key, options });
                }
            }
        },

        // Extension mutations - Event Manager
        updateEventManagerCategoryOptionsLoading(value) {
            Vue.set(this.options.extensions.eventManager.category, 'loading', value);
        },

        insertEventManagerCategoryOptions(categories) {
            Vue.set(this.options.extensions.eventManager.category, 'items', categories);
        },

        setEventManagerSectionCategories({ s, categories }) {
            Vue.set(this.form.sections[s], 'eventCategories', categories);
        },

        // Extension mutations - Shop
        updateShopCategoryOptionsLoading(value) {
            Vue.set(this.options.extensions.shop.category, 'loading', value);
        },

        insertShopCategoryOptions(categories) {
            Vue.set(this.options.extensions.shop.category, 'items', categories);
        },

        updateShopTagOptionsLoading(value) {
            Vue.set(this.options.extensions.shop.tag, 'loading', value);
        },

        insertShopTagOptions(tags) {
            Vue.set(this.options.extensions.shop.tag, 'items', tags);
        },

        updateShopPropertyOptionsLoading(value) {
            Vue.set(this.options.extensions.shop.property, 'loading', value);
        },

        insertShopPropertyOptions(properties) {
            Vue.set(this.options.extensions.shop.property, 'items', properties);
        },

        updateShopCustomerGroupOptionsLoading(value) {
            Vue.set(this.options.extensions.shop.customerGroup, 'loading', value);
        },

        insertShopCustomerGroupOptions(customerGroups) {
            Vue.set(this.options.extensions.shop.customerGroup, 'items', customerGroups);
        },

        setShopSectionCategories({ s, categories }) {
            Vue.set(this.form.sections[s], 'shopCategories', categories);
        },

        // Extension mutations - Marketing
        updateMarketingCampaignOptionsLoading(value) {
            Vue.set(this.options.extensions.marketing.campaign, 'loading', value);
        },

        insertMarketingCampaignOptions(campaigns) {
            Vue.set(this.options.extensions.marketing.campaign, 'items', campaigns);
        },

        // Extension mutations - Newsletter
        updateNewsletterSubscriptionTopicOptionsLoading(value) {
            Vue.set(this.options.extensions.newsletter.subscriptionTopic, 'loading', value);
        },

        insertNewsletterSubscriptionTopicOptions(topics) {
            Vue.set(this.options.extensions.newsletter.subscriptionTopic, 'items', topics);
        },

        // Extension mutations - Restricted Area
        updateRestrictedAreaMemberTagOptionsLoading(value) {
            Vue.set(this.options.extensions.restrictedArea.memberTag, 'loading', value);
        },

        insertRestrictedAreaMemberTagOptions(tags) {
            Vue.set(this.options.extensions.restrictedArea.memberTag, 'items', tags);
        },

        // Async actions
        fetchCategoryPagesPaginated() {
            return PageApi.fetchPagesPaginated(this.categoryPagesPaginated).then(data => {
                this.addPaginatedCategoryPagesToState(data);
                const activeColumnKeys = map(data.config.activeColumnKeys, 'key');
                this.defineActiveColumns(activeColumnKeys);
            });
        },

        fetchCategoryPage(id) {
            const setupStore = useSetupStore(pinia);
            const cms = setupStore.activeCms;
            if (cms) {
                return PageApi.fetchPage(id, cms.id).then(categoryPage => {
                    this.insertPage(categoryPage);
                    this.initializeTeaser();
                });
            }
        },

        insertPage(page) {
            this.setState({ path: 'form', value: cloneDeep(defaultForm) });

            const value = cloneDeep(page);

            const sections = get(page, 'sections') || [];
            if (sections.length) {
                value.sections = sections.filter(section => !section.gridArea);

                const handleRemoteContent = section => {
                    const setupStore = useSetupStore(pinia);
                    const configs = get(setupStore.activeCms, 'config.template.sections', false);
                    if (configs) {
                        const remoteContentSection = configs.find(setting => setting.key === 'remotecontent');
                        if (remoteContentSection && remoteContentSection.settings) {
                            remoteContentSection.settings.forEach(setting => {
                                const { remote, request_type, token } = setting;

                                axios.post(remote, { token })
                                    .then(response => {
                                        let sectionIndex, settingIndex;
                                        sectionIndex = configs.findIndex(section => section.key === 'remotecontent');

                                        if (setting.key === 'categories') {
                                            let remoteContentCategoryOptions = [];
                                            response.data.categories.forEach(category => {
                                                remoteContentCategoryOptions.push({ key: category.uid, value: category.name });
                                            });

                                            settingIndex = configs[sectionIndex].settings
                                                .findIndex(setting => setting.key === 'categories');

                                            setupStore.addOptionsToSectionSettings({ sectionIndex, settingIndex, options: remoteContentCategoryOptions });

                                        } else if (setting.key === 'groups') {
                                            let remoteContentTargetGroupOptions = [];
                                            response.data.zielgruppen.forEach(targetgroup => {
                                                remoteContentTargetGroupOptions.push({ key: targetgroup.uid, value: targetgroup.name });
                                            });

                                            settingIndex = configs[sectionIndex].settings
                                                .findIndex(setting => setting.key === 'groups');

                                            setupStore.addOptionsToSectionSettings({ sectionIndex, settingIndex, options: remoteContentTargetGroupOptions });
                                        }
                                    })
                                    .catch(error => {
                                        console.error(error);
                                    });
                            });
                        }
                    }
                };

                const populateGrid = grid => {
                    if (!grid.gridAreas || !grid.gridAreas.length) return;

                    grid.gridAreas.forEach(area => {
                        const areaSections = sections.filter(section => _.get(section.gridArea, 'id') === area.id);
                        if (areaSections.length) {
                            area.gridItems = areaSections;
                            walkSections(area.gridItems);
                        }
                    });
                };

                const walkSections = sections => {
                    let position = 1;
                    sections.sort((a, b) => get(a, 'position', 0) - get(b, 'position', 0)).forEach(section => {
                        if (section.type === 'grid') {
                            populateGrid(section);
                        } else if (section.key === 'remotecontent') {
                            handleRemoteContent(section);
                        }
                        section.position = position;
                        position++;
                    });
                };

                walkSections(value.sections);
            }

            this.setState({ path: 'form', value });
        },

        findCategoryPosts(query) {
            const setupStore = useSetupStore(pinia);
            const cms = setupStore.activeCms;
            const categories = get(cms, 'config.template.categories') || [];
            return PageApi.fetchPagesPaginated({
                pageInfo: { perPage: 100 },
                variables: {
                    order: 'created_at',
                    direction: 'desc'
                },
                filters: [
                    { key: 'title', like: query },
                    { key: 'cms', match: cms.id },
                    { key: 'category_keys', in: categories.map(el => el.key) }
                ]
            });
        },

        fetchCategoryOptions(cmsID) {
            const setupStore = useSetupStore(pinia);
            const cms = setupStore.activeCms;
            const templateCategories = get(cms, 'config.template.categories') || [];

            this.updateCategoryOptionsLoading(true);
            return CategoryApi.fetchList(cmsID)
                .then(persistedCategories => {
                    const merged = templateCategories.map(templateCategory => {
                        const persisted = persistedCategories.find(item => item.key === templateCategory.key);
                        return persisted || { key: templateCategory.key, name: templateCategory.name };
                    });

                    persistedCategories.forEach(persisted => {
                        if (!merged.find(item => item.key === persisted.key)) merged.push(persisted);
                    });

                    this.updateCategoryOptionsLoading(false);
                    this.insertCategoryOptions(merged);
                })
                .catch(err => {
                    this.updateCategoryOptionsLoading(false);
                    throw err;
                });
        },

        fetchMenuOptions(cmsID) {
            this.updateMenuOptionsLoading(true);
            return MenuApi.fetchList(cmsID)
                .then(menus => {
                    this.updateMenuOptionsLoading(false);
                    this.insertMenuOptions(menus);
                })
                .catch(err => {
                    this.updateMenuOptionsLoading(false);
                    throw err;
                });
        },

        fetchTagOptions(cmsID) {
            this.updateTagOptionsLoading(true);
            return TagApi.fetchList(cmsID)
                .then(tags => {
                    this.updateTagOptionsLoading(false);
                    this.insertTagOptions(tags);
                })
                .catch(err => {
                    this.updateTagOptionsLoading(false);
                    throw err;
                });
        },

        fetchFileTagOptions(cmsID) {
            this.updateFileTagOptionsLoading(true);
            return FileApi.fetchTagList(cmsID)
                .then(tags => {
                    this.updateFileTagOptionsLoading(false);
                    this.insertFileTagOptions(tags);
                })
                .catch(err => {
                    this.updateFileTagOptionsLoading(false);
                    throw err;
                });
        },

        fetchFilterOptions({ cms, category }) {
            this.updateFilterOptionsLoading(true);
            return CategoryFiltersAPI.fetchList(cms, category)
                .then(filters => {
                    this.updateFilterOptionsLoading(false);
                    this.insertFilterOptions(filters);
                })
                .catch(err => {
                    this.updateFilterOptionsLoading(false);
                    throw err;
                });
        },

        initializeCategoryPage(key) {
            const setupStore = useSetupStore(pinia);
            const cms = setupStore.activeCms;
            if (cms) {
                this.setFormCreatedAt(getUnixTime(new Date()));

                const categoryConfigs = get(cms, 'config.template.categories');
                const categoryConfig = categoryConfigs.find(category => category.key === key);

                const category = this.options.category.items.find(category => category.key === key)
                    || { key, name: get(categoryConfig, 'name') || key };
                this.setFormCategory(category);

                const views = get(categoryConfig, 'views');
                this.insertViewOptions(views);

                if (views && views.length) {
                    const defaultView = views.find(view => view.default === true);
                    if (defaultView) this.setFormView(defaultView.value);
                    else this.setFormView(views[0].value);
                }

                const pageConfigs = get(cms, 'config.template.pages');
                const sectionConfigs = get(cms, 'config.template.sections') || [];

                const addSections = (sectionsConfigs, area = 'main') => {
                    sectionsConfigs.forEach(config => {
                        if (config.default === true || config.min > 0) {
                            const sectionConfig = sectionConfigs.find(sectionConfig => sectionConfig.key === config.key);
                            if (sectionConfig) {
                                config.name = sectionConfig.name;
                                config.contents = cloneDeep(sectionConfig.contents);
                                config.area = area;

                                const s = get(this.form, 'sections.length', 1) - 1;
                                this.addSection({ s, section: config });
                            }
                        }
                    });
                };

                if (pageConfigs.length && this.form.view) {
                    const pageConfig = pageConfigs.find(page => page.view === this.form.view);

                    const areaConfigs = get(pageConfig, 'grid.areas') || [];

                    if (!areaConfigs.length) {
                        let sectionsConfigs = get(pageConfig, 'sections') || [];
                        sectionsConfigs = cloneDeep(sectionsConfigs).sort((a, b) => get(a, 'position', 0) - get(b, 'position', 0));
                        addSections(sectionsConfigs);
                    } else {
                        areaConfigs.forEach(area => {
                            let sectionsConfigs = get(area, 'sections') || [];
                            sectionsConfigs = cloneDeep(sectionsConfigs).sort(
                                (a, b) => get(a, 'position', 0) - get(b, 'position', 0)
                            );
                            addSections(sectionsConfigs, area.key);
                        });
                    }
                }
            }
        },

        initializeTeaser() {
            const setupStore = useSetupStore(pinia);
            const cms = setupStore.activeCms;
            if (cms) {
                let config = get(cms, 'config.template.teaser');
                let teaser = get(this.form, 'sections', []).find(section => section.key === 'category-page-teaser');

                if (config && !teaser) {
                    config = cloneDeep(config);
                    config.area = 'teaser';
                    config.id = 'new:teaser';

                    this.addSection({ s: -1, section: config });
                }
            }
        },

        saveSection({ path, section }) {
            if (startsWith(section.id, 'new:')) {
                return this.createSection({ path, section });
            } else {
                return this.updateSection({ path, section });
            }
        },

        createSection({ path, section }) {
            if (!this.isGlobalContentsMode && !this.form.id) return Promise.reject('No page id');
            if (section.locked) return Promise.reject('section is locked');

            if (path === undefined) path = 'sections';

            const payload = {
                ...section,
                gridArea: null,
            };

            if (!this.isGlobalContentsMode) {
                payload.page = { id: this.form.id };
            }

            this.updateSectionLoading({ path, section, value: true });

            let address = path.split('.');
            if (address.length > 1) {
                address.pop();
                const gridAreaPath = address.join('.');
                const gridArea = get(this.form, gridAreaPath);
                if (gridArea) {
                    payload.gridArea = {
                        id: gridArea.id
                    };
                }
            }

            const setupStore = useSetupStore(pinia);
            const cms = setupStore.activeCms;
            return SectionApi.create(payload, cms)
                .then(newSection => {
                    this.updateSectionLoading({ path, section: newSection, value: false });
                    newSection = restoreMissingMediaFileData(payload, newSection);
                    this.replaceSection({ path, oldSection: section, newSection });
                })
                .catch(err => {
                    this.updateSectionLoading({ path, section, value: false });
                    this.deleteSection({ path, section });
                    throw err;
                });
        },

        updateSection({ path, section }) {
            if (!this.isGlobalContentsMode && !this.form.id) return Promise.reject('No page id');
            if (section.locked) return Promise.reject('section is locked');

            if (path === undefined) path = 'sections';

            const payload = {
                ...section,
                gridArea: null,
            };

            this.updateSectionLoading({ path, section, value: true });

            let address = path.split('.');
            if (address.length > 1) {
                address.pop();
                const gridAreaPath = address.join('.');
                const gridArea = get(this.form, gridAreaPath);
                if (gridArea) {
                    payload.gridArea = {
                        id: gridArea.id
                    };
                }
            }

            return SectionApi.update(payload)
                .catch(err => {
                    throw err;
                })
                .finally(() => {
                    this.updateSectionLoading({ path, section, value: false });
                });
        },

        pasteSection({ path, sectionId, pageId, position, gridAreaId, area }) {
            return SectionApi.clone(sectionId, pageId, position, gridAreaId, area)
                .then(allClonedSections => {
                    if (path === undefined) path = 'sections';
                    const sections = get(this.form, path) || [];

                    // The flat list contains the cloned section + any grid items.
                    // The "root" cloned section is the one that belongs directly
                    // in this path (either no gridArea for top-level, or matching
                    // gridAreaId for grid-item paste). All other sections are
                    // nested grid items that need to be distributed into gridAreas.
                    const rootSections = gridAreaId
                        ? allClonedSections.filter(section => _.get(section, 'gridArea.id') === gridAreaId)
                        : allClonedSections.filter(section => !section.gridArea);

                    // Distribute grid items into their grid areas (same pattern as insertPage)
                    const populateGrid = grid => {
                        if (!grid.gridAreas || !grid.gridAreas.length) return;
                        grid.gridAreas.forEach(gridArea => {
                            const areaSections = allClonedSections.filter(
                                section => _.get(section, 'gridArea.id') === gridArea.id
                            );
                            if (areaSections.length) {
                                gridArea.gridItems = areaSections;
                                areaSections.sort((a, b) => get(a, 'position', 0) - get(b, 'position', 0));
                                areaSections.forEach(section => {
                                    if (section.type === 'grid') {
                                        populateGrid(section);
                                    }
                                });
                            }
                        });
                    };

                    // Only populate nested grids — do NOT overwrite root positions
                    // (they come from the backend which already reindexed correctly)
                    rootSections.forEach(section => {
                        if (section.type === 'grid') {
                            populateGrid(section);
                        }
                    });

                    // Shift local positions of existing sections at or after the target position
                    // Only shift sections in the same area
                    sections.forEach(section => {
                        if ((!area || section.area === area) && section.position >= position) {
                            Vue.set(section, 'position', section.position + 1);
                        }
                    });

                    // Add the cloned root sections reactively
                    rootSections.forEach(clonedSection => {
                        set(this.form, path + '.' + (get(this.form, path) || []).length, clonedSection);
                    });

                    // Sort by position to get correct visual order
                    const updated = get(this.form, path) || [];
                    updated.sort((a, b) => get(a, 'position', 0) - get(b, 'position', 0));

                    // Re-normalize positions (1-based)
                    this.sortSections({ path, sections: updated });

                    return rootSections[0];
                });
        },

        deleteSectionAction({ path, section }) {
            if (section.locked) return Promise.reject('section is locked');

            this.updateSectionLoading({ path, section, value: true });

            return SectionApi.delete(section.id)
                .then(() => {
                    this.deleteSection({ path, section });
                    Vue.nextTick(() => {
                        const remainingSectionsInArea = (get(this.form, path) || [])
                            .filter(el => el && el.area === section.area)
                            .slice()
                            .sort((a, b) => get(a, 'position', 0) - get(b, 'position', 0));
                        this.sortSections({ path, sections: remainingSectionsInArea });
                    });
                })
                .catch(err => {
                    throw err;
                })
                .finally(() => {
                    this.updateSectionLoading({ path, section, value: false });
                });
        },

        reorganizeSections({ level, area, path, sections }) {
            if (!sections) return;

            if (path === undefined) path = 'sections';
            const currentSections = get(this.form, path) || [];
            let gridAreaPayload = null;

            if (path !== 'sections') {
                let address = path.split('.');
                if (address.length > 1) {
                    address.pop();
                    const gridAreaPath = address.join('.');
                    const gridArea = get(this.form, gridAreaPath);
                    if (gridArea) {
                        gridAreaPayload = {
                            id: gridArea.id,
                            key: gridArea.key,
                            name: gridArea.name,
                            row: cloneDeep(gridArea.row),
                            column: cloneDeep(gridArea.column),
                            images: cloneDeep(gridArea.images),
                        };
                    }
                }
            }

            sections.forEach((section, index) => {
                const updateSection = () => {
                    if (!startsWith(section.id, 'new:')) {
                        const payload = {
                            id: section.id,
                            position: index + 1,
                            type: section.type,
                            area
                        };
                        this.updateSection({ path, section: payload });
                    }
                };

                let s = currentSections.findIndex(el => el.id === section.id);
                if (s >= 0) {
                    // section exists in original list
                } else {
                    if (!currentSections.length) {
                        this.setState({ path: `form.${path}`, value: [] });
                    }

                    s = currentSections.length;
                    this.setState({ path: `form.${path}.${s}`, value: section });

                    updateSection();
                }

                this.setState({ path: `form.${path}.${s}.position`, value: index + 1 });
                this.setState({ path: `form.${path}.${s}.gridArea`, value: gridAreaPayload });
                if (section.area !== area) {
                    this.setState({ path: `form.${path}.${s}.area`, value: area });
                    updateSection();
                }
            });

            if (sections.length < currentSections.length) {
                const findSection = (section, collection, lvl) => {
                    return collection.some(el => {
                        if (!el) return false;
                        if (level !== lvl && section.id === el.id) {
                            return section;
                        } else if (el.type === 'grid') {
                            const items = el.gridAreas.map(el => el.gridItems).flat() || [];
                            return findSection(section, items, lvl + 1);
                        }
                    });
                };

                const rootSections = get(this.form, 'sections') || [];
                currentSections.forEach((section, index) => {
                    let s = sections.findIndex(el => el.id === section.id);
                    if (s < 0 && (level > 1 || findSection(section, rootSections, 1))) {
                        this.deleteSection({ path, section });
                    }
                });
            }
        },

        saveMultipleSections(sections) {
            if (sections && sections.length) {
                SectionApi.saveMany(sections);
            }
        },

        ensurePageCategoryPersisted(page) {
            if (!page || !page.category || page.category.id || !page.category.key) {
                return Promise.resolve(page);
            }

            const setupStore = useSetupStore(pinia);
            const cms = setupStore.activeCms;
            if (!cms) return Promise.resolve(page);

            return CategoryApi.create({
                key: page.category.key,
                name: page.category.name || page.category.key,
                cms: { id: cms.id }
            }).then(createdCategory => {
                page.category = createdCategory;
                this.setFormCategory(createdCategory);

                const categoryOptions = cloneDeep(this.options.category.items);
                const index = categoryOptions.findIndex(item => item.key === createdCategory.key);
                if (index >= 0) categoryOptions[index] = createdCategory;
                else categoryOptions.push(createdCategory);
                this.insertCategoryOptions(categoryOptions);

                return page;
            });
        },

        save(page) {
            return this.ensurePageCategoryPersisted(page).then(page => {
                if (!page.id || startsWith(page.id, 'new:')) {
                    return this.create(page);
                } else {
                    return this.update(page);
                }
            });
        },

        create(page) {
            return PageApi.create(page).then(page => {
                this.resetRules();
                return page;
            });
        },

        update(page) {
            return PageApi.update(page).then(page => {
                this.resetRules();
                return page;
            });
        },

        extendedSave(page) {
            return this.ensurePageCategoryPersisted(page).then(page => {
                if (!page.id || startsWith(page.id, 'new:')) {
                    return PageApi.create(page, true).then(page => {
                        this.resetRules();
                        return page;
                    });
                } else {
                    return PageApi.update(page, true).then(page => {
                        this.resetRules();
                        return page;
                    });
                }
            });
        },

        delete(id) {
            return PageApi.delete(id).then(() => {
                this.reset();
                this.resetRules();
                this.resetUI();
            });
        },

        reset() {
            this.insertPage(cloneDeep(defaultForm));
            this.setState({ path: 'teaser', value: undefined });
        },

        // Extension actions - Event Manager
        fetchEventManagerCategoryOptions(cmsID) {
            this.updateEventManagerCategoryOptionsLoading(true);
            return EXT_EVNT_CategoryApi.fetchList(cmsID)
                .then(categories => {
                    this.updateEventManagerCategoryOptionsLoading(false);
                    this.insertEventManagerCategoryOptions(categories);
                })
                .catch(err => {
                    this.updateEventManagerCategoryOptionsLoading(false);
                    console.warn('Failed to fetch event manager category options:', err);
                });
        },

        // Extension actions - Shop
        fetchShopCategoryOptions(shopID) {
            this.updateShopCategoryOptionsLoading(true);
            return EXT_SHOP_CategoryApi.fetchList(shopID)
                .then(categories => {
                    this.updateShopCategoryOptionsLoading(false);
                    this.insertShopCategoryOptions(categories);
                })
                .catch(err => {
                    this.updateShopCategoryOptionsLoading(false);
                    console.warn('Failed to fetch shop category options:', err);
                });
        },

        fetchShopPropertyOptions(shopID) {
            this.updateShopPropertyOptionsLoading(true);
            return EXT_SHOP_PropertyApi.fetchList(shopID)
                .then(properties => {
                    this.updateShopPropertyOptionsLoading(false);
                    this.insertShopPropertyOptions(properties);
                })
                .catch(err => {
                    this.updateShopPropertyOptionsLoading(false);
                    console.warn('Failed to fetch shop property options:', err);
                });
        },

        fetchShopTagOptions(shopID) {
            this.updateShopTagOptionsLoading(true);
            return EXT_SHOP_TagApi.fetchList(shopID)
                .then(tags => {
                    this.updateShopTagOptionsLoading(false);
                    this.insertShopTagOptions(tags);
                })
                .catch(err => {
                    this.updateShopTagOptionsLoading(false);
                    console.warn('Failed to fetch shop tag options:', err);
                });
        },

        fetchShopCustomerGroupOptions(shopID) {
            this.updateShopCustomerGroupOptionsLoading(true);
            return EXT_SHOP_CustomerGroupApi.fetchList(shopID)
                .then(customerGroups => {
                    this.updateShopCustomerGroupOptionsLoading(false);
                    this.insertShopCustomerGroupOptions(customerGroups);
                })
                .catch(err => {
                    this.updateShopCustomerGroupOptionsLoading(false);
                    console.warn('Failed to fetch shop customer group options:', err);
                });
        },

        // Extension actions - Marketing
        fetchMarketingCampaignOptions(cmsID) {
            this.updateMarketingCampaignOptionsLoading(true);
            return EXT_MARKETING_CampaignApi.fetchList(cmsID)
                .then(campaigns => {
                    this.updateMarketingCampaignOptionsLoading(false);
                    this.insertMarketingCampaignOptions(campaigns);
                })
                .catch(err => {
                    this.updateMarketingCampaignOptionsLoading(false);
                    console.warn('Failed to fetch marketing campaign options:', err);
                });
        },

        // Extension actions - Newsletter
        fetchNewsletterSubscriptionTopicOptions(cmsID) {
            this.updateNewsletterSubscriptionTopicOptionsLoading(true);
            return EXT_NEWSLETTER_SubscriptionTopicApi.fetchList(cmsID)
                .then(topics => {
                    this.updateNewsletterSubscriptionTopicOptionsLoading(false);
                    this.insertNewsletterSubscriptionTopicOptions(topics);
                })
                .catch(err => {
                    this.updateNewsletterSubscriptionTopicOptionsLoading(false);
                    console.warn('Failed to fetch newsletter subscription topic options:', err);
                });
        },

        // Extension actions - Restricted Area
        fetchRestrictedAreaMemberTagOptions(cmsID) {
            this.updateRestrictedAreaMemberTagOptionsLoading(true);
            return MemberTagApi.fetchList(cmsID)
                .then(tags => {
                    this.updateRestrictedAreaMemberTagOptionsLoading(false);
                    this.insertRestrictedAreaMemberTagOptions(tags);
                })
                .catch(err => {
                    this.updateRestrictedAreaMemberTagOptionsLoading(false);
                    console.warn('Failed to fetch restricted area member tag options:', err);
                });
        },
    }
});
